import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button/Button';
import { Input } from '../ui/Input/Input';
import { StepNavigation } from '../StepNavigation/StepNavigation';
import { PlusIcon, TrashIcon, PreviewIcon, CloudIcon, EditIcon, UploadIcon, DownloadIcon, GridIcon, ListIcon, CsvIcon, FolderIcon } from '../Icons/Icons';
import { PreviewModal } from '../PreviewModal/PreviewModal';
import { GoogleDriveFolderBrowser } from '../GoogleDrive/GoogleDriveBrowser';
import LocalFolderBrowser from '../LocalFolderBrowser';
import { ProductTableView } from './ProductTableView';
import { useEvaluationStore } from '../../store/evaluationStore';
import { useUserSettingsStore } from '../../store/userSettingsStore';
import { encryptProductTitle, decryptProductTitle } from '../../utils/encryption';
import { GoogleDriveService } from '../../services/GoogleDriveService';
import LocalFileService from '../../services/LocalFileService';
import type { GoogleDriveFile } from '../../types/googleDrive';
import type { LocalFile } from '../../services/LocalFileService';
import { ProductDetailsDialog } from './ProductRegistration/ProductDetailsDialog';

export const ProductRegistration: React.FC = () => {
  const { t } = useTranslation();
  const { products, addProduct, updateProduct, removeProduct, setCurrentStep, saveDataAsync } = useEvaluationStore();
  const { userSettings } = useUserSettingsStore();
  
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
  const [editingData, setEditingData] = React.useState({ title: '', link: '', comment: '' });
  const [addingNew, setAddingNew] = React.useState(false);
  const [decryptedTitles, setDecryptedTitles] = React.useState<Map<number, string>>(new Map());
  const [viewMode, setViewMode] = React.useState<'form' | 'table'>('form');
  const [previewModal, setPreviewModal] = React.useState<{ 
    isOpen: boolean; 
    url: string; 
    title: string;
    productIndex?: number;
  }>({
    isOpen: false,
    url: '',
    title: ''
  });
  const [googleDriveModal, setGoogleDriveModal] = React.useState(false);
  const [localFolderModal, setLocalFolderModal] = React.useState(false);
  const csvInputRef = React.useRef<HTMLInputElement>(null);
  const [showDetailsDialog, setShowDetailsDialog] = React.useState(false);
  const [detailsQuestions, setDetailsQuestions] = React.useState<{ reviewTitle: string; reviewLink?: string; reviewComment: string }[]>([]);
  const [quickInputMode, setQuickInputMode] = React.useState(false);
  const [quickInputValue, setQuickInputValue] = React.useState('');
  const quickInputRef = React.useRef<HTMLInputElement>(null);

  // セキュリティモードの状態を取得
  const securityModeEnabled = userSettings?.securityMode || false;

  // 評価対象名の復号化（表示用）
  React.useEffect(() => {
    if (securityModeEnabled && userSettings?.privateKey) {
      const decryptTitles = async () => {
        const newDecryptedTitles = new Map<number, string>();
        
        for (let i = 0; i < products.length; i++) {
          const product = products[i];
          if (product.reviewTitle.startsWith('encrypted:')) {
            try {
              const decrypted = await decryptProductTitle(product.reviewTitle, userSettings.privateKey!);
              newDecryptedTitles.set(i, decrypted);
            } catch (error) {
              console.error(`${t('productRegistration.error.encryptionLog')} ${i}:`, error);
              newDecryptedTitles.set(i, `[${t('common.error')}]`);
            }
          } else {
            newDecryptedTitles.set(i, product.reviewTitle);
          }
        }
        
        setDecryptedTitles(newDecryptedTitles);
      };
      
      decryptTitles();
    } else {
      // セキュリティモードが無効な場合は、そのまま表示
      const plainTitles = new Map<number, string>();
      products.forEach((product, index) => {
        plainTitles.set(index, product.reviewTitle);
      });
      setDecryptedTitles(plainTitles);
    }
  }, [products, securityModeEnabled, userSettings?.privateKey]);

  // インライン編集開始
  const startInlineEdit = (index: number) => {
    const product = products[index];
    setEditingIndex(index);
    setEditingData({
      title: decryptedTitles.get(index) || product.reviewTitle,
      link: product.reviewLink || '',
      comment: product.reviewComment || ''
    });
  };

  // 新規追加開始
  const startAddNew = () => {
    setAddingNew(true);
    setEditingData({ title: '', link: '', comment: '' });
  };

  // 編集キャンセル
  const cancelEdit = () => {
    setEditingIndex(null);
    setAddingNew(false);
    setEditingData({ title: '', link: '', comment: '' });
  };

  // 編集保存
  const saveEdit = async () => {
    if (!editingData.title.trim() || !editingData.comment.trim()) return;

    let titleToStore = editingData.title.trim();
    
    // セキュリティモードが有効な場合は暗号化
    if (securityModeEnabled && userSettings?.publicKey) {
      try {
        titleToStore = await encryptProductTitle(titleToStore, userSettings.publicKey);
      } catch (error) {
        console.error(t('productRegistration.error.encryptionError'), error);
        alert(t('productRegistration.error.encryptionFailed'));
        return;
      }
    }

    const productData = {
      reviewTitle: titleToStore,
      reviewLink: editingData.link.trim() || undefined,
      reviewComment: editingData.comment.trim()
    };

    if (addingNew) {
      addProduct(productData);
      setAddingNew(false);
    } else if (editingIndex !== null) {
      updateProduct(editingIndex, productData);
      setEditingIndex(null);
    }

    setEditingData({ title: '', link: '', comment: '' });
  };

  // プレビューを開く
  const openPreview = (product: { reviewTitle: string; reviewLink?: string; reviewComment: string }, index: number) => {
    if (!product.reviewLink) return;
    
    setPreviewModal({
      isOpen: true,
      url: product.reviewLink,
      title: decryptedTitles.get(index) || product.reviewTitle,
      productIndex: index
    });
  };

  // プレビューを閉じる
  const closePreview = () => {
    setPreviewModal({
      isOpen: false,
      url: '',
      title: ''
    });
  };

  // プレビューナビゲート
  const handleNavigatePreview = (direction: 'prev' | 'next') => {
    if (previewModal.productIndex === undefined) return;
    
    let newIndex = previewModal.productIndex;
    
    if (direction === 'prev' && newIndex > 0) {
      newIndex--;
    } else if (direction === 'next' && newIndex < products.length - 1) {
      newIndex++;
    }
    
    if (newIndex !== previewModal.productIndex && products[newIndex].reviewLink) {
      openPreview(products[newIndex], newIndex);
    }
  };

  // CSVテンプレートダウンロード
  const downloadCsvTemplate = () => {
    const csvContent = [
      ['タイトル', 'リンク', '詳細コメント'].join(','),
      ['例：製品A', 'https://example.com/product-a', '製品Aの詳細説明'].join(',')
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'evaluation_targets_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // CSVアップロード
  const handleCsvUploadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        
        // ヘッダー行をスキップ
        for (let i = 1; i < lines.length; i++) {
          const [title, link, comment] = lines[i].split(',').map(s => s.trim());
          
          if (title && comment) {
            let titleToStore = title;
            
            if (securityModeEnabled && userSettings?.publicKey) {
              try {
                titleToStore = await encryptProductTitle(title, userSettings.publicKey);
              } catch (error) {
                console.error(t('error.encryptionError'), error);
                continue;
              }
            }
            
            addProduct({
              reviewTitle: titleToStore,
              reviewLink: link || undefined,
              reviewComment: comment
            });
          }
        }
        
        await saveDataAsync();
      };
      reader.readAsText(file);
    };
    input.click();
  };

  // Google Drive インポート
  const handleGoogleDriveImport = async (files: GoogleDriveFile[]) => {
    const service = GoogleDriveService.getInstance();
    
    for (const file of files) {
      if (file.mimeType === 'text/csv') {
        try {
          const content = await service.downloadFile(file.id);
          const lines = content.split('\n').filter(line => line.trim());
          
          for (let i = 1; i < lines.length; i++) {
            const [title, link, comment] = lines[i].split(',').map((s: string) => s.trim());
            if (title && comment) {
              let titleToStore = title;
              
              if (securityModeEnabled && userSettings?.publicKey) {
                try {
                  titleToStore = await encryptProductTitle(title, userSettings.publicKey);
                } catch (error) {
                  console.error(t('error.encryptionError'), error);
                  continue;
                }
              }
              
              addProduct({
                reviewTitle: titleToStore,
                reviewLink: link || undefined,
                reviewComment: comment
              });
            }
          }
        } catch (error) {
          console.error(t('error.googleDriveImportError'), error);
        }
      } else {
        let titleToStore = file.name;
        
        if (securityModeEnabled && userSettings?.publicKey) {
          try {
            titleToStore = await encryptProductTitle(file.name, userSettings.publicKey);
          } catch (error) {
            console.error(t('error.encryptionError'), error);
            continue;
          }
        }
        
        addProduct({
          reviewTitle: titleToStore,
          reviewLink: file.webViewLink,
          reviewComment: `Google Drive: ${file.name}`
        });
      }
    }
    
    await saveDataAsync();
  };

  // ローカルファイル インポート
  const handleLocalFilesImport = async (files: LocalFile[]) => {
    const service = LocalFileService.getInstance();
    
    for (const file of files) {
      const metadata = service.getFileMetadata(file.file);
      
      if (metadata.isCSV) {
        try {
          const data = await service.parseCSV(file.file);
          
          // ヘッダー行をスキップ
          for (let i = 1; i < data.length; i++) {
            const [title, link, comment] = data[i];
            if (title && comment) {
              let titleToStore = title;
              
              if (securityModeEnabled && userSettings?.publicKey) {
                try {
                  titleToStore = await encryptProductTitle(title, userSettings.publicKey);
                } catch (error) {
                  console.error(t('error.encryptionError'), error);
                  continue;
                }
              }
              
              addProduct({
                reviewTitle: titleToStore,
                reviewLink: link || undefined,
                reviewComment: comment
              });
            }
          }
        } catch (error) {
          console.error(t('error.csvParseError'), error);
        }
      } else {
        let titleToStore = file.name;
        
        if (securityModeEnabled && userSettings?.publicKey) {
          try {
            titleToStore = await encryptProductTitle(file.name, userSettings.publicKey);
          } catch (error) {
            console.error(t('error.encryptionError'), error);
            continue;
          }
        }
        
        // プレビューURLを生成（対応ファイルのみ）
        const previewUrl = await service.createPreviewUrl(file.file);
        
        addProduct({
          reviewTitle: titleToStore,
          reviewLink: previewUrl || undefined,
          reviewComment: ''  // コメントは空にする
        });
      }
    }
    
    await saveDataAsync();
  };

  // クイック入力の処理
  const handleQuickInput = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && quickInputValue.trim()) {
      let titleToStore = quickInputValue.trim();
      
      if (securityModeEnabled && userSettings?.publicKey) {
        try {
          titleToStore = await encryptProductTitle(titleToStore, userSettings.publicKey);
        } catch (error) {
          console.error(t('error.encryptionError'), error);
          alert(t('productRegistration.error.encryptionFailed'));
          return;
        }
      }
      
      addProduct({
        reviewTitle: titleToStore,
        reviewComment: quickInputValue.trim()
      });
      
      setQuickInputValue('');
      
      // 5個ごとに詳細入力を促す
      const newTotalProducts = products.length + 1;
      if (newTotalProducts % 5 === 0) {
        const needsDetails = products.slice(-5).concat([{
          reviewTitle: titleToStore,
          reviewComment: quickInputValue.trim()
        }]);
        setDetailsQuestions(needsDetails);
        setShowDetailsDialog(true);
      }
      
      await saveDataAsync();
    }
  };

  const handleNext = () => {
    setCurrentStep(2);
  };

  // クリックイベントの伝播を停止
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div className="section">
      <StepNavigation 
        currentStep={1}
        canProceed={products.length > 0}
        onNext={handleNext}
        onPrev={() => {}}
      />
      <div className="section__content">
        <div className="section__title-wrapper">
          <h2 className="section__title section__title--centered">
            {t('productRegistration.title')}
          </h2>
          <p className="section__description section__description--centered">
            {t('productRegistration.description')}
          </p>
        </div>


        {/* クイック入力モード */}
        {quickInputMode && (
          <div className="quick-input-container">
            <div className="quick-input-wrapper">
              <input
                ref={quickInputRef}
                type="text"
                className="quick-input"
                placeholder={t('productRegistration.quickInput.placeholder')}
                value={quickInputValue}
                onChange={(e) => setQuickInputValue(e.target.value)}
                onKeyDown={handleQuickInput}
                autoFocus
              />
            </div>
            <p className="quick-input-hint">
              {t('productRegistration.quickInput.hint')}
            </p>
          </div>
        )}

        {/* 入力モード切替ボタン */}
        <div className="section__header-actions">
          <Button
            variant={quickInputMode ? "ghost" : "secondary"}
            size="sm"
            onClick={() => {
              setQuickInputMode(!quickInputMode);
              if (!quickInputMode) {
                setTimeout(() => quickInputRef.current?.focus(), 100);
              }
            }}
          >
            {quickInputMode ? t('productRegistration.quickInput.disable') : t('productRegistration.quickInput.enable')}
          </Button>
        </div>

        {(
          <>
            {products.length > 0 ? (
              <h3 className="product-list__title">
                {t('productRegistration.list.title', { count: products.length })}
                {securityModeEnabled && (
                  <span className="security-badge">{t('productRegistration.list.encryptedBadge')}</span>
                )}
              </h3>
            ) : null}
            <div className="product-grid-container">
              <div className="product-grid">
              {/* 追加ボタンカード */}
              <div 
                className="product-card product-card--add"
                onClick={startAddNew}
              >
                {addingNew ? (
                  // 新規追加フォーム
                  <div className="product-card__edit-form" onClick={stopPropagation}>
                    <div className="product-card__edit-field">
                      <input
                        type="text"
                        className="product-card__edit-input"
                        placeholder={t('productRegistration.form.titlePlaceholder')}
                        value={editingData.title}
                        onChange={(e) => setEditingData({ ...editingData, title: e.target.value })}
                        autoFocus
                      />
                    </div>
                    <div className="product-card__edit-field">
                      <input
                        type="url"
                        className="product-card__edit-input"
                        placeholder={t('productRegistration.form.linkPlaceholder')}
                        value={editingData.link}
                        onChange={(e) => setEditingData({ ...editingData, link: e.target.value })}
                      />
                    </div>
                    <div className="product-card__edit-field">
                      <textarea
                        className="product-card__edit-textarea"
                        placeholder={t('productRegistration.form.detailPlaceholder')}
                        value={editingData.comment}
                        onChange={(e) => setEditingData({ ...editingData, comment: e.target.value })}
                      />
                    </div>
                    <div className="product-card__edit-actions">
                      <button
                        className="product-card__edit-btn product-card__edit-btn--save"
                        onClick={saveEdit}
                        disabled={!editingData.title.trim() || !editingData.comment.trim()}
                      >
                        {t('common.save')}
                      </button>
                      <button
                        className="product-card__edit-btn product-card__edit-btn--cancel"
                        onClick={cancelEdit}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="product-card__add-content">
                    <span className="product-card__add-icon">
                      <PlusIcon />
                    </span>
                    <span className="product-card__add-text">{t('productRegistration.form.addButton')}</span>
                  </div>
                )}
              </div>

              {/* 評価対象カード */}
              {products.map((product, index) => (
                <div 
                  key={index} 
                  className={`product-card ${editingIndex === index ? 'product-card--editing' : ''}`}
                  onClick={() => editingIndex !== index && startInlineEdit(index)}
                >
                  {editingIndex === index ? (
                    // インライン編集フォーム
                    <div className="product-card__edit-form" onClick={stopPropagation}>
                      <div className="product-card__edit-field">
                        <input
                          type="text"
                          className="product-card__edit-input"
                          placeholder={t('productRegistration.form.titlePlaceholder')}
                          value={editingData.title}
                          onChange={(e) => setEditingData({ ...editingData, title: e.target.value })}
                          autoFocus
                        />
                      </div>
                      <div className="product-card__edit-field">
                        <input
                          type="url"
                          className="product-card__edit-input"
                          placeholder={t('productRegistration.form.linkPlaceholder')}
                          value={editingData.link}
                          onChange={(e) => setEditingData({ ...editingData, link: e.target.value })}
                        />
                      </div>
                      <div className="product-card__edit-field">
                        <textarea
                          className="product-card__edit-textarea"
                          placeholder={t('productRegistration.form.detailPlaceholder')}
                          value={editingData.comment}
                          onChange={(e) => setEditingData({ ...editingData, comment: e.target.value })}
                        />
                      </div>
                      <div className="product-card__edit-actions">
                        <button
                          className="product-card__edit-btn product-card__edit-btn--save"
                          onClick={saveEdit}
                          disabled={!editingData.title.trim() || !editingData.comment.trim()}
                        >
                          {t('common.save')}
                        </button>
                        <button
                          className="product-card__edit-btn product-card__edit-btn--cancel"
                          onClick={cancelEdit}
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // 通常表示
                    <div className="product-card__content">
                      <h4 className="product-card__title">
                        {decryptedTitles.get(index) || product.reviewTitle}
                        {securityModeEnabled && (
                          <span className="encryption-indicator" title={t('productRegistration.list.encrypted')}>
                            🔒
                          </span>
                        )}
                      </h4>
                      <p className="product-card__detail">
                        {product.reviewComment?.substring(0, 30)}...
                      </p>
                      <div className="product-card__overlay">
                        <div className="product-card__actions">
                        {product.reviewLink && (
                          <button
                            className="icon-button icon-button--sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPreview(product, index);
                            }}
                            title={t('productRegistration.preview', { title: decryptedTitles.get(index) || product.reviewTitle })}
                            aria-label={t('productRegistration.preview', { title: decryptedTitles.get(index) || product.reviewTitle })}
                          >
                            <PreviewIcon />
                          </button>
                        )}
                        <button
                          className="icon-button icon-button--sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            startInlineEdit(index);
                          }}
                          title={t('productRegistration.edit', { title: decryptedTitles.get(index) || product.reviewTitle })}
                          aria-label={t('productRegistration.edit', { title: decryptedTitles.get(index) || product.reviewTitle })}
                        >
                          <EditIcon />
                        </button>
                        <button
                          className="icon-button icon-button--sm icon-button--danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeProduct(index);
                          }}
                          title={t('productRegistration.delete', { title: decryptedTitles.get(index) || product.reviewTitle })}
                          aria-label={t('productRegistration.delete', { title: decryptedTitles.get(index) || product.reviewTitle })}
                        >
                          <TrashIcon />
                        </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              </div>
            </div>
          </>
        )}
      </div>

      <PreviewModal
        isOpen={previewModal.isOpen}
        onClose={closePreview}
        url={previewModal.url}
        title={previewModal.title}
        productIndex={previewModal.productIndex}
        onNavigate={handleNavigatePreview}
      />

      <GoogleDriveFolderBrowser
        isOpen={googleDriveModal}
        onClose={() => setGoogleDriveModal(false)}
        onFilesSelected={handleGoogleDriveImport}
      />

      <LocalFolderBrowser
        visible={localFolderModal}
        onCancel={() => setLocalFolderModal(false)}
        onSelect={handleLocalFilesImport}
        multiple={true}
      />

      {showDetailsDialog && (
        <ProductDetailsDialog
          isOpen={showDetailsDialog}
          onClose={() => {
            setShowDetailsDialog(false);
            setDetailsQuestions([]);
          }}
          onComplete={(details) => {
            details.forEach((detail) => {
              updateProduct(detail.index, {
                ...products[detail.index],
                reviewComment: detail.detail,
                reviewLink: detail.link
              });
            });
            setShowDetailsDialog(false);
            setDetailsQuestions([]);
          }}
          questions={detailsQuestions.map((product, idx) => ({
            index: products.length - detailsQuestions.length + idx,
            title: decryptedTitles.get(products.length - detailsQuestions.length + idx) || product.reviewTitle,
            currentDetail: product.reviewComment || '',
            currentLink: product.reviewLink
          }))}
        />
      )}

      {/* CSV入力 (hidden) */}
      <input
        ref={csvInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={() => {}}
      />


      {/* Import/Export Actions */}
      <div className="import-export-container">
        <div className="import-export-section">
          <h3 className="import-export-title">{t('productRegistration.import.sectionTitle')}</h3>
          <div className="import-export-buttons">
            <button
              className="import-export-button"
              onClick={() => setGoogleDriveModal(true)}
              title={t('productRegistration.import.driveTitle')}
              aria-label={t('productRegistration.import.driveLabel')}
            >
              <CloudIcon />
              <span>{t('productRegistration.import.googleDrive')}</span>
            </button>
            <button
              className="import-export-button"
              onClick={() => setLocalFolderModal(true)}
              title={t('productRegistration.import.localTitle')}
              aria-label={t('productRegistration.import.localLabel')}
            >
              <FolderIcon />
              <span>{t('productRegistration.import.local')}</span>
            </button>
            <button
              className="import-export-button"
              onClick={handleCsvUploadClick}
              title={t('productRegistration.import.csvTitle')}
              aria-label={t('productRegistration.import.csvLabel')}
            >
              <UploadIcon />
              <span>{t('productRegistration.import.csvShort')}</span>
            </button>
          </div>
        </div>
        <div className="import-export-section">
          <h3 className="import-export-title">{t('productRegistration.export.sectionTitle')}</h3>
          <div className="import-export-buttons">
            <button
              className="import-export-button"
              onClick={downloadCsvTemplate}
              title={t('productRegistration.import.templateTitle')}
              aria-label={t('productRegistration.import.templateLabel')}
            >
              <DownloadIcon />
              <span>{t('productRegistration.export.template')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};